import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";
import { Check, Loader2 } from "lucide-react";

// Poll for up to 30 attempts at 2s each = 60s max
const MAX_ATTEMPTS = 30;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const loc = useLocation();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | paid | timeout | error | unpaid
  const [nextPath, setNextPath] = useState("/results");
  const attemptRef = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const sessionId = params.get("session_id");
    const next = params.get("next") || "/results";
    setNextPath(next);
    if (!sessionId) { setStatus("error"); return; }

    let timer;
    const poll = async () => {
      if (attemptRef.current >= MAX_ATTEMPTS) { setStatus("timeout"); return; }
      attemptRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh?.();
          track("checkout_completed", { plan_type: "premium" });
          setStatus("paid");
          setTimeout(() => nav(data.return_path || next), 1400);
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("unpaid"); return; }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        setStatus("error");
      }
    };
    poll();
    return () => { if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Nav />
      <main className="container-hs py-16 max-w-lg text-center">
        {status === "loading" && (
          <div data-testid="pay-loading" className="card-hs p-10">
            <Loader2 className="mx-auto mb-4 animate-spin text-brand-accent" size={36} />
            <h1 className="font-display text-2xl text-brand-ink">Confirming your payment…</h1>
            <p className="text-brand-muted mt-2 text-sm">This usually takes a few seconds. Please don't close this tab.</p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="pay-success" className="card-hs p-10">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand-soft text-brand-accent mb-4"><Check size={26} /></div>
            <h1 className="font-display text-2xl text-brand-ink">Your 90-day Home Buying Pass is active</h1>
            <p className="text-brand-muted mt-2 text-sm">Returning you to where you left off…</p>
          </div>
        )}
        {status === "timeout" && (
          <div data-testid="pay-timeout" className="card-hs p-10">
            <h1 className="font-display text-2xl text-brand-ink">Still processing</h1>
            <p className="text-brand-muted mt-2 text-sm">Your payment is taking a little longer than usual. Refresh in a moment or check My Homes.</p>
            <button onClick={() => nav(nextPath)} className="btn-primary mt-6">Continue anyway</button>
          </div>
        )}
        {(status === "unpaid" || status === "error") && (
          <div data-testid="pay-error" className="card-hs p-10">
            <h1 className="font-display text-2xl text-brand-ink">We couldn't confirm your payment</h1>
            <p className="text-brand-muted mt-2 text-sm">If you were charged, your access will unlock automatically within a few minutes.</p>
            <button onClick={() => nav("/upgrade")} className="btn-primary mt-6">Back to upgrade</button>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}
